/**
 * BulkTransactionRowCard Component
 *
 * 거래 일괄 입력(A-7)의 카드 표현 — 좁은 화면(<768px)에서 사용.
 *
 * @why 좁은 화면에서 8개 열을 가로로 늘어놓으면 가로 스크롤과 오터치가 심해진다.
 * 한 행을 카드 하나로 세워 라벨과 오류 문구를 각 칸 옆에 붙인다.
 *
 * 이 컴포넌트는 상태를 갖지 않는다. 값을 받아 그리고 변경을 위로 올려보내기만 하며,
 * 상태·검증·저장은 테이블 표현과 100% 공유한다.
 */

import { memo, useMemo } from 'react'
import { Input } from '@/shared/components/ui/Input'
import { Select } from '@/shared/components/ui/Select'
import { DatePicker } from '@/shared/components/ui/DatePicker'
import type {
  BulkRowViewProps,
  BulkTransactionDraftRow,
} from '../types/BulkTransaction'

/** 해당 필드의 오류 메시지 (없으면 undefined) */
function errorOf(
  props: BulkRowViewProps,
  field: keyof BulkTransactionDraftRow
): string | undefined {
  return props.validation?.errors.find((e) => e.field === field)?.message
}

function BulkTransactionRowCardBase(props: BulkRowViewProps) {
  const {
    row,
    rowNumber,
    validation,
    expenseCategories,
    incomeCategories,
    paymentMethodOptions,
    userOptions,
    onChange,
    onRemove,
    disabled,
  } = props

  const isIncome = row.type === 'income'

  const majorCategories = isIncome ? incomeCategories : expenseCategories

  const majorOptions = useMemo(
    () =>
      majorCategories.map((cat) => ({
        value: cat.id,
        label: cat.name,
        icon: cat.icon,
      })),
    [majorCategories]
  )

  const minorOptions = useMemo(() => {
    const major = majorCategories.find((cat) => cat.id === row.majorCategoryId)
    return (major?.children || []).map((cat) => ({
      value: cat.id,
      label: cat.name,
      icon: cat.icon,
    }))
  }, [majorCategories, row.majorCategoryId])

  const hasError = (validation?.errors.length ?? 0) > 0

  // 지출=결제 계좌 소유자 / 수입=받은 사람. 같은 필드지만 의미가 달라 라벨을 바꾼다.
  const spenderLabel = isIncome ? '받은 사람' : '결제 계좌 소유자'

  const set = (patch: Partial<BulkTransactionDraftRow>) => onChange(row.clientId, patch)

  return (
    <li
      className={`rounded-xl border p-4 space-y-4 bg-white dark:bg-gray-800 ${
        hasError
          ? 'border-red-400 dark:border-red-500'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* 헤더: 행 번호 · 수입/지출 전환 · 삭제 */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {rowNumber}행
        </span>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            {(['expense', 'income'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => set({ type })}
                disabled={disabled}
                aria-pressed={row.type === type}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  row.type === type
                    ? type === 'expense'
                      ? 'bg-expense text-white'
                      : 'bg-income text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                }`}
              >
                {type === 'expense' ? '지출' : '수입'}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => onRemove(row.clientId)}
            disabled={disabled}
            aria-label={`${rowNumber}행 삭제`}
            className="px-2 py-1.5 text-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
          >
            삭제
          </button>
        </div>
      </div>

      <DatePicker
        label="날짜"
        value={row.date}
        onChange={(date) => set({ date })}
        required
        {...(errorOf(props, 'date') ? { error: errorOf(props, 'date')! } : {})}
      />

      <Input
        label="금액"
        type="text"
        inputMode="numeric"
        value={row.amount}
        onChange={(amount) => set({ amount })}
        placeholder="0"
        required
        disabled={disabled}
        {...(errorOf(props, 'amount') ? { error: errorOf(props, 'amount')! } : {})}
      />

      <Select
        label="대분류"
        value={row.majorCategoryId}
        onChange={(majorCategoryId) => set({ majorCategoryId })}
        options={majorOptions}
        placeholder="대분류 선택"
        required
        disabled={disabled}
        {...(errorOf(props, 'majorCategoryId')
          ? { error: errorOf(props, 'majorCategoryId')! }
          : {})}
      />

      <Select
        label="소분류 (선택사항)"
        value={row.minorCategoryId}
        onChange={(minorCategoryId) => set({ minorCategoryId })}
        options={minorOptions}
        placeholder="소분류 선택"
        disabled={disabled || !row.majorCategoryId || minorOptions.length === 0}
        {...(errorOf(props, 'minorCategoryId')
          ? { error: errorOf(props, 'minorCategoryId')! }
          : {})}
      />

      {/* 수입은 "입금"이라 결제수단 개념이 없어 칸 자체를 띄우지 않는다 */}
      {!isIncome && (
        <Select
          label="결제수단"
          value={row.paymentMethodId}
          onChange={(paymentMethodId) => set({ paymentMethodId })}
          options={paymentMethodOptions}
          placeholder="결제수단 선택"
          required
          disabled={disabled}
          {...(errorOf(props, 'paymentMethodId')
            ? { error: errorOf(props, 'paymentMethodId')! }
            : {})}
        />
      )}

      <Select
        label={spenderLabel}
        value={row.spenderId}
        onChange={(spenderId) => set({ spenderId })}
        options={userOptions}
        placeholder={`${spenderLabel} 선택`}
        required
        disabled={disabled}
        {...(errorOf(props, 'spenderId')
          ? { error: errorOf(props, 'spenderId')! }
          : {})}
      />

      <Input
        label="메모 (선택사항)"
        value={row.memo}
        onChange={(memo) => set({ memo })}
        placeholder="거래 메모"
        disabled={disabled}
      />
    </li>
  )
}

/**
 * @why 최대 50행이 동시에 존재할 수 있다. memo가 없으면 한 칸을 고칠 때마다
 * 모든 행이 다시 그려져 입력이 눈에 띄게 밀린다.
 * (부모가 넘기는 콜백과 선택지 배열은 안정적인 참조로 유지된다)
 */
export const BulkTransactionRowCard = memo(BulkTransactionRowCardBase)
